<template>
  <div ref="coverRef" class="upload-cover-wrapper">
    <el-upload
      v-if="!readonly"
      :width="width"
      :height="height"
      name="file"
      :data="{ label: uploadLabel }"
      :headers="{ Authorization:`Bearer ${getToken()}` }"
      :action="action"
      :accept="imageTypeStrs"
      :show-file-list="false"
      :on-error="() => { loading = false }"
      :before-upload="onBeforeUpload"
      :on-success="(res: IAxiosResponse<any>) => { resolveUploaded(res) }"
      :disabled="disabled"
    >
      <div v-if="loading" class="cover-loading">
        <svg-icon class="no-bg" icon="line-md:loading-loop" />
      </div>

      <img v-if="succeedImage" class="cover-img" :src="succeedImage">
      <div v-else class="upload-box">
        <svg-icon icon="tabler:plus" />
      </div>

      <div v-if="!loading && succeedImage" title="重新上传" class="cover-loading upload-tip-icon">
        <svg-icon v-if="!disabled" icon="tabler:plus" />
        <svg-icon
          title="预览大图"
          class="no-bg ml-3 hover:opacity-60"
          icon="solar:magnifer-zoom-in-outline"
          @click="(e) => {
            e.stopPropagation();
            e.preventDefault();
            openImagePreviewByUrl(succeedImage)
          }"
        />
      </div>
    </el-upload>

    <template v-else>
      <img v-if="succeedImage" class="cover-img" :src="succeedImage">
      <div v-if="succeedImage" title="预览大图" class="cover-loading upload-tip-icon" @click="openImagePreviewByUrl(succeedImage)">
        <svg-icon class="no-bg" icon="solar:magnifer-zoom-in-outline" />
      </div>
    </template>

    <image-editor
      :visible="imagePanelVisible"
      :image="imageBase64"
      :saving="uploading"
      @close="imagePanelVisible = false"
      @change="doUploadToServer"
    />
  </div>
</template>

<script setup lang="ts">
import SvgIcon from '../SvgIcon.vue';
import ImageEditor from '../image-editor/ImageEditor.vue';
import { EAxiosResponseCode, getToken, IAxiosResponse, settings } from '@core/api';
import { imageBase64ToFile, imageFileToBase64, imageTypes, imageTypeStrs, loadScript } from '@core/tools';
import { ElMessage, UploadProps } from 'element-plus';
import { openImagePreviewByUrl } from '../hooks';
import { computed, onMounted, ref } from 'vue';
import { uploadImgToServer } from './uploadApi';

const emit = defineEmits<{'on-success': [obj: any]}>();

const loading = ref(false);
const coverRef = ref<HTMLDivElement>();

defineOptions({ name: 'CardImageUpload' });

const props = defineProps({
  action: {
    type: String,
    required: true,
  },
  image: {
    type: String,
    default: '',
  },
  width: {
    type: Number,
    default: 314,
  },
  height: {
    type: Number,
    default: 202,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  placeholderImage: {
    type: String,
    default: 'none',
  },
  uploadLabel: {
    type: String,
    default: '',
  },
  maxFileSize: {
    type: Number,
    default: 20,
  },
  enableImageEditor: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const imagePanelVisible = ref(false);
const uploadedImage = ref('');
const imageBase64 = ref('');
const uploading = ref(false);

const boxWidth = computed(() => `${props.width}px`);
const boxHeight = computed(() => `${props.height}px`);
const imgWidth = computed(() => `${props.width - 2}px`);
const imgHeiht = computed(() => `${props.height - 2}px`);
const boxPlaceholderImg = computed(() => `url(${props.placeholderImage})`);
const succeedImage = computed(() => uploadedImage.value || props.image);

const resolveUploaded = (res: IAxiosResponse<any>) => {
  const { code, context, message } = res || {};

  loading.value = false;

  if (String(code) === EAxiosResponseCode.Succeed) {
    emit('on-success', context);
  } else {
    ElMessage.error(message);
  }
};

const onBeforeUpload: UploadProps['beforeUpload'] = async (file) => {
  loading.value = false;

  if (!imageTypes.includes(file.type)) {
    ElMessage.error('只能上传图片格式文件');
    return false;
  } else if (file.size / 1024 / 1024 > props.maxFileSize) {
    ElMessage.error(`图片大小不能超过${props.maxFileSize}MB`);
    return false;
  } else if (props.enableImageEditor) {
    const base64 = await imageFileToBase64(file);

    if (!base64) {
      ElMessage.error('图片上传失败');
      return false;
    }

    imagePanelVisible.value = true;
    imageBase64.value = base64 as string;

    return false;
  }

  loading.value = true;
  return true;
};

const doUploadToServer = async (base64: string) => {
  uploading.value = true;

  try {
    const res = await uploadImgToServer(imageBase64ToFile(base64), props.action);

    imagePanelVisible.value = false;
    emit('on-success', res);
  } catch (error) {
    if (error instanceof Error) ElMessage.error(error.message);
  }

  uploading.value = false;
};

onMounted(() => {
  if (props.enableImageEditor) {
    loadScript(
      settings.imageEditorScriptUrl,
      settings.imageEditorScriptKey,
    );
  }
});
</script>

<style scoped lang="scss">
.upload-box {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: v-bind(boxWidth);
  height: v-bind(boxHeight);
  border-radius: 2px;
  background-repeat: no-repeat;
  background-image: v-bind(boxPlaceholderImg);

  .svg-icon {
    color: #B8BFC3;
    width: 26px;
    height: 26px;
    border: 1px solid var(--color-border-base);
    border-radius: 50%;
    padding: 4px;
  }
}

.cover-img {
  width: v-bind(imgWidth);
  max-height: 98%;
  max-width: 99%;
  height: v-bind(imgHeiht);
  object-fit: contain;
  border-radius: 2px;
  cursor: pointer;
}

.cover-loading {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 2px;
  width: v-bind(boxWidth);
  height: v-bind(boxHeight);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, .2);
  color: #fff;
  transition: all .3s;

  .svg-icon {
    border: 1px #fff solid;
    border-radius: 50%;
    background-color: #fff;
    color: #95A0B6;
    box-sizing: border-box;
    padding: 3px;
  }

  .svg-icon.no-bg {
    background-color: transparent;
    border: none;
    color: #fff;
    padding: 0;
    width: 26px;
    height: 26px;
  }
}

.upload-tip-icon {
  display: none;
  cursor: pointer;
}

.upload-cover-wrapper {
  border: 1px solid #eee;
  border-radius: 2px;
  width: v-bind(boxWidth);
  height: v-bind(boxHeight);
  position: relative;
  padding-bottom: 5px;

  :deep(.el-upload) {
    width: v-bind(boxWidth);
    height: v-bind(boxHeight);
  }

  &:hover {
   .upload-tip-icon {
      display: flex;
    }
  }
}
</style>
